import { useCallback, useEffect, useState } from 'react';

import {
  type ShelfSurface,
  trackShelfItemAdded,
  trackShelfItemRemoved,
} from '@/analytics/events';
import type {
  AddToShelfMutation,
  AddToShelfMutationVariables,
  RemoveFromShelfMutation,
  RemoveFromShelfMutationVariables,
} from '@/api/generated/graphql';
import { ADD_TO_SHELF_MUTATION, REMOVE_FROM_SHELF_MUTATION } from '@/api/mutations/shelf';
import { useAuthedClient } from '@/api/useAuthedClient';

import { useGraphQLMutation } from './useGraphQLMutation';

interface UseShelfToggleReturn {
  isOnShelf: boolean;
  isToggling: boolean;
  // Failure of the most recent toggle attempt; cleared when the next attempt
  // starts. Consumers may ignore it (CoffeeDetailPage relies on the
  // optimistic revert alone; AccountPage's cards render it inline).
  error: Error | null;
  // Resolves true on success so callers can chain follow-up work (e.g.
  // refetching a shelf list); false when the mutation failed and the
  // optimistic flip was reverted.
  toggle: () => Promise<boolean>;
}

export function useShelfToggle(
  initialIsOnShelf: boolean,
  coffeeId: string,
  // The screen this toggle lives on, for `shelf_item_*` analytics. Passed
  // explicitly (not inferred) so reusing the hook from a new surface can't
  // silently mis-attribute events.
  surface: ShelfSurface,
): UseShelfToggleReturn {
  const [isOnShelf, setIsOnShelf] = useState(initialIsOnShelf);
  const authedClient = useAuthedClient();

  useEffect(() => {
    setIsOnShelf(initialIsOnShelf);
  }, [initialIsOnShelf]);

  const { mutate, isSubmitting, error } = useGraphQLMutation<
    boolean,
    unknown,
    { wasOnShelf: boolean }
  >({
    mutationFn: async (wasOnShelf, signal) => {
      if (wasOnShelf) {
        return authedClient.request<RemoveFromShelfMutation, RemoveFromShelfMutationVariables>({
          document: REMOVE_FROM_SHELF_MUTATION,
          variables: { input: { coffeeId } },
          signal,
        });
      }
      return authedClient.request<AddToShelfMutation, AddToShelfMutationVariables>({
        document: ADD_TO_SHELF_MUTATION,
        variables: { input: { coffeeId } },
        signal,
      });
    },
    onMutate: (wasOnShelf) => {
      setIsOnShelf(!wasOnShelf);
      return { wasOnShelf };
    },
    onError: (_err, _input, ctx) => {
      setIsOnShelf(ctx.wasOnShelf);
    },
  });

  const toggle = useCallback(async () => {
    const wasOnShelf = isOnShelf;
    // Fire analytics success-only (non-null result) — a failed mutation
    // reverts the optimistic UI via onError and must not leave a phantom
    // shelf event behind.
    const result = await mutate(wasOnShelf);
    if (!result) return false;
    if (wasOnShelf) {
      trackShelfItemRemoved({ coffee_id: coffeeId, surface });
    } else {
      trackShelfItemAdded({ coffee_id: coffeeId, surface });
    }
    return true;
  }, [coffeeId, isOnShelf, mutate, surface]);

  return { isOnShelf, isToggling: isSubmitting, error, toggle };
}
